import React, { Component } from "react";
import { Form, Button } from "react-bootstrap";
import { withRouter } from "react-router-dom";
import { graphql } from "react-apollo";
import gql from "graphql-tag";
import * as Cookies from "es-cookie";
import client from "../../apolloClient";
import { getFirstAccessibleRoute } from "../../moduleAccessGuard";
import { resolveUserScope } from "../../utils/dataScope";

import vignanLogo from "../../images/vignan_logo.png";
import imgAbout from "../../images/vignan/abtt.webp";
import imgMPC from "../../images/vignan/future-2.jpg";
import imgBiPC from "../../images/vignan/bipc.webp";
import imgIITRep from "../../images/vignan/long-term.webp";
import imgNEETRep from "../../images/vignan/future-1.jpg";
import "../../_vignan_login.scss";

const VIGNAN_COURSES = [
  { img: imgMPC, title: "Intermediate MPC", desc: "With IIT-JEE Main & Advanced coaching." },
  { img: imgBiPC, title: "Intermediate BiPC", desc: "With integrated NEET preparation." },
  { img: imgIITRep, title: "Long-Term IIT-JEE", desc: "Repeater program for JEE aspirants." },
  { img: imgNEETRep, title: "Long-Term NEET", desc: "Repeater program for NEET aspirants." },
];

const VIGNAN_CAMPUSES = [
  "Nizampet", "Medchal", "Thimmapuram", "Vadlamudi", "Palakaluru",
  "Dilsukhnagar", "AS Rao Nagar", "Benz Circle", "Dwaraka Nagar",
];

// Vignan Junior College branding (ref: https://vignanjuniorcolleges.org/).
const VIGNAN_NAME = "Vignan Junior College";
const VIGNAN_TAGLINE = "Empowering Students to Achieve Academic Excellence";
const VIGNAN_CONTACT = "+91 906 314 8877";
const VIGNAN_EMAIL = "vignan.jrconnect@vignan.edu.in";
const VIGNAN_ADDRESS = "Venkatraya Nagar, Nizampet, Hyderabad, Telangana 500090";
const VIGNAN_REGISTER_URL = "https://vignanjuniorcolleges.org/";

const GET_ORGANISATION_PROFILE = gql`
  query($institution_id: Int!) {
    getOrganisationProfile(institution_id: $institution_id) {
      id
      complete_address
      logo
    }
  }
`;

const GET_ROLE_MODULE_ACCESS = gql`
  query($institute_id: Int, $employee_role_id: Int) {
    getRoleModuleAccess(institute_id: $institute_id, employee_role_id: $employee_role_id) {
      module_id
      module_name
      module_key
      sub_module_name
      is_view
      is_edit
      is_delete
    }
  }
`;

// Main college (institute admin) modules: the college's enabled modules for this
// employee_type at this institute. Used to build the admin sidebar from
// employee_type_id + institute_id rather than the raw module_access JSON.
const GET_MODULES_BY_EMPLOYEE_TYPE = gql`
  query($institute_id: Int, $employee_type_id: Int) {
    getModulesByEmployeeType(institute_id: $institute_id, employee_type_id: $employee_type_id) {
      module_id
      module_name
      display_name
      is_view
    }
  }
`;

const LOGIN_USER = gql`
  mutation($username: String!, $password: String!) {
    login1(username: $username, password: $password) {
      token
      refreshToken
      user {
        username
        userlevel
        email
        mobile
        valid
        name
        institution_id
        institute_name
        module_access
        subject_ids
        class_ids
        section_ids
        branch_id
        employee_role_id
        id
      }
    }
  }
`;

class VignanLogin extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentStep: 1,
      username: "",
      password: "",
      submitError: "",
      formErrors: {
        username: "",
        password: ""
      },
      usernameValid: false,
      passwordValid: false,
      formValid: false,
      loader: 0,
      showPassword: false
    };
  }
  togglePasswordVisibility = () => {
    this.setState(prevState => ({ showPassword: !prevState.showPassword }));
  };
  handleFormSubmit = e => {
    e.preventDefault();
    this.setState({
      loader: 1
    });

    if (this.state.formValid) {
      this.login1(this.state.username, this.state.password).catch(error => {
        this.setState({
          submitError: error.graphQLErrors.map(x => x.message), loader: 0
        });
        console.error(
          "ERR =>",
          error.graphQLErrors.map(x => x.message)
        );
      });
    } else {
      this.setState({ submitError: "Please fill all the values to proceed", loader: 0 });
    }
  };
  login1 = async (username, password) => {
    const result = await this.props.login1({
      variables: {
        username,
        password
      },
      update: (store, { data }) => {
        Cookies.set("token", data.login1.token);
        Cookies.set("refreshtoken", data.login1.refreshToken);
        Cookies.set("username", data.login1.user.username);
        Cookies.set("name", data.login1.user.name);
        Cookies.set("email", data.login1.user.email);
        Cookies.set("id", data.login1.user.id);
        Cookies.set("institutionid", data.login1.user.institution_id);
        Cookies.set("role", "institution");
        Cookies.set("institutename", data.login1.user.institute_name);
        Cookies.set("userlevel", data.login1.user.userlevel);
        // Logged-in employee's assigned sections (CSV, same convention as
        // subject_ids/class_ids). Empty = unassigned → not scoped by section.
        Cookies.set("section_ids", String(data.login1.user.section_ids || ""));
        localStorage.setItem("moduleRestrictions", JSON.stringify(data.login1.user.module_access));
      }
    });

    // After mutation completes, set branch_id from the backend natively
    const userData = result.data.login1.user;
    Cookies.set("branch_id", String(userData.branch_id || ""));
    Cookies.set("employee_role_id", String(userData.employee_role_id || ""));

    // Capture the employee's assignment (branch / class / subject ids) so every
    // module can show only the records matching them.
    await resolveUserScope(client, userData);

    // Fetch role-based module access for this user's branch + role and cache it
    try {
      const accessResult = await client.query({
        query: GET_ROLE_MODULE_ACCESS,
        variables: {
          institute_id: parseInt(userData.institution_id),
          employee_role_id: userData.employee_role_id ? parseInt(userData.employee_role_id) : null,
        },
        fetchPolicy: "network-only",
      });
      const roleAccess = (accessResult.data && accessResult.data.getRoleModuleAccess) || [];
      localStorage.setItem("roleModuleAccess", JSON.stringify(roleAccess));

      // Role-based menu: when the user has a configured role, the sidebar must show
      // ONLY the modules that role is granted (is_view) — and only among the modules
      // this college has access to (getRoleModuleAccess already restricts the list to
      // the institute's accessible modules). We translate that into the SAME
      // `moduleRestrictions` shape the sidebar already reads (key === false => visible),
      // so no other module/menu logic changes.
      // Guard: only override when the user actually has a role AND that role grants at
      // least one viewable module. Otherwise leave the institute-level access as-is, so
      // admins and roles without configured access are unaffected (no impact).
      // The main college login (institute admin, employee_type_id / userlevel === 1) must
      // ALWAYS see the full set of the college's accessible modules — never the
      // role-restricted subset — even if that admin happens to have a role assigned.
      const isAdmin = parseInt(userData.userlevel) === 1;
      const hasRole = userData.employee_role_id && parseInt(userData.employee_role_id) > 0;
      if (isAdmin) {
        // Main college login: show the college's enabled modules for this
        // employee_type + institute (getModulesByEmployeeType returns only the
        // accessible ones). Build moduleRestrictions with every returned module
        // visible (key === false). If it returns nothing, leave the raw
        // module_access from login as-is.
        const typeResult = await client.query({
          query: GET_MODULES_BY_EMPLOYEE_TYPE,
          variables: {
            institute_id: parseInt(userData.institution_id),
            employee_type_id: parseInt(userData.userlevel),
          },
          fetchPolicy: "network-only",
        });
        const typeModules = (typeResult.data && typeResult.data.getModulesByEmployeeType) || [];
        if (typeModules.length > 0) {
          const effective = {};
          typeModules.forEach((r) => {
            effective[r.module_name] = false; // false = visible in sidebar
          });
          localStorage.setItem("moduleRestrictions", JSON.stringify(effective));
        }
      } else if (hasRole && roleAccess.length > 0) {
        const effective = {};
        roleAccess.forEach((r) => {
          const canView = r.is_view === true || r.is_view === 1 || r.is_view === "1";
          effective[r.module_name] = canView ? false : true; // false = visible in sidebar
        });
        const anyVisible = Object.values(effective).some((v) => v === false);
        if (anyVisible) {
          localStorage.setItem("moduleRestrictions", JSON.stringify(effective));
        }
      }
    } catch (accessError) {
      console.error("Error fetching role module access:", accessError);
      localStorage.setItem("roleModuleAccess", JSON.stringify([]));
    }

    // Fetch organisation profile and store address & url (logo) in cookies
    try {
      const orgResult = await client.query({
        query: GET_ORGANISATION_PROFILE,
        variables: { institution_id: parseInt(userData.institution_id) },
        fetchPolicy: "network-only",
      });
      const org = orgResult.data && orgResult.data.getOrganisationProfile;
      if (org) {
        Cookies.set("address", org.complete_address || "");
        Cookies.set(
          "url",
          org.logo ? `https://support.rizee.in/files/${org.logo}` : ""
        );
      }
    } catch (orgError) {
      console.error("Error fetching organisation profile:", orgError);
    }

    this.setState({ loader: 0 });
    if (result.data.login1) {
      // ── OLD CODE (kept for reference / rollback) ──────────────────────────
      // Everyone landed on the Dashboard, granted or not.
      //
      // this.props.history.push("/dashboard");

      // ── NEW CODE ─────────────────────────────────────────────────────────
      // First module this role actually has View access to. See Login.jsx for the note.
      this.props.history.push(getFirstAccessibleRoute());
    }
  };
  handleInputChange = e => {
    const name = e.target.name;
    const value = e.target.value;
    this.setState({ [name]: value }, () => {
      this.validateField(name, value);
    });
  };
  validateField(fieldName, value) {
    let fieldValidationErrors = this.state.formErrors;
    let usernameValid = this.state.usernameValid;
    let passwordValid = this.state.passwordValid;

    switch (fieldName) {
      case "username":
        if (value.length < 4) {
          usernameValid = false;
          fieldValidationErrors.username =
            "Username cannot be less than 5 chars";
        } else {
          usernameValid = true;
          fieldValidationErrors.username = "";
        }
        break;
      case "password":
        if (value.length == "") {
          passwordValid = false;
          fieldValidationErrors.password = "Password cannot be Empty";
        } else {
          passwordValid = true;
          fieldValidationErrors.password = "";
        }
        break;
      default:
        break;
    }
    this.setState(
      {
        formErrors: fieldValidationErrors,
        usernameValid: usernameValid,
        passwordValid: passwordValid
      },
      this.validateForm
    );
  }

  validateForm() {
    this.setState({
      formValid: this.state.passwordValid || this.state.usernameValid
    });
    if (this.state.formValid) {
      this.setState({ submitError: "" }, () => { });
    }
  }

  render() {
    if (
      Cookies.get("token") != undefined &&
      Cookies.get("institutionid") != undefined
    ) {
      // Land on the first module the user actually has View access to (Dashboard for
      // those who have it), not a hardcoded route that could 403 for restricted roles.
      this.props.history.push(getFirstAccessibleRoute());
    }

    return (
      <div className="vignan_login">
        <div className="vignan_shell">
          {/* LEFT — intro / marketing panel */}
          <div className="vignan_intro">
            <a href={VIGNAN_REGISTER_URL}>
              <img
                className="vignan_logo"
                src={vignanLogo}
                alt={`${VIGNAN_NAME} Logo`}
              />
            </a>
            <h1>{VIGNAN_TAGLINE}</h1>
            <p className="vignan_intro_sub">
              A premier institution for IIT-JEE, NEET &amp; SAT preparation —
              guiding students to top ranks with expert faculty and a proven track record.
            </p>
            <ul className="vignan_intro_list">
              <li><i className="fas fa-graduation-cap" /> IIT-JEE, NEET &amp; SAT focused coaching</li>
              <li><i className="fas fa-building" /> Multiple campuses across AP &amp; Telangana</li>
              <li><i className="fas fa-users" /> Empowering 2000+ students every year</li>
              <li><i className="fas fa-chart-line" /> Real-time exams &amp; performance tracking</li>
            </ul>
          </div>

          {/* RIGHT — login card */}
          <div className="vignan_card">
          <div className="vignan_card_head">
            <h1>Welcome <span className="accent">Back</span></h1>
            <p>Sign in to your account to continue</p>
          </div>

          <Form>
            {this.state.currentStep == 5 ? (
              <span className="vignan_error">You are successfully loggedIn</span>
            ) : (
              this.state.submitError ? <span className="vignan_error">{this.state.submitError}</span> : null
            )}

            <Form.Group className="mb-3" controlId="formBasicEmail">
              <Form.Label className="vignan_label"> <i className="far fa-envelope mr-2" /> UserName / Email</Form.Label>
              <Form.Control type="text" className="input_custom" placeholder="Enter Name" onChange={this.handleInputChange} name="username" />
              {this.state.formErrors.username ? (
                <span className="vignan_field_error">{this.state.formErrors.username}</span>
              ) : null}
            </Form.Group>

            <Form.Group className="mb-3" controlId="formBasicPassword">
              <Form.Label className="vignan_label"> <i className="fas fa-lock mr-2" /> Password</Form.Label>
              <div className="vignan_password_wrap">
                <Form.Control type={this.state.showPassword ? "text" : "password"} className="input_custom" placeholder="Password" onChange={this.handleInputChange} name="password" />
                <i
                  className={`vignan_eye fas ${this.state.showPassword ? "fa-eye-slash" : "fa-eye"}`}
                  onClick={this.togglePasswordVisibility}
                />
              </div>
              {this.state.formErrors.password ? (
                <span className="vignan_field_error">{this.state.formErrors.password}</span>
              ) : null}
            </Form.Group>

            {this.state.loader == 1 ? (
              <Button className="btn-vignan" type="submit" disabled>
                <span className="spinner-border spinner-border-sm mr-2"></span>
                logging..
              </Button>
            ) : (
              <Button onClick={this.handleFormSubmit} className="btn-vignan" type="submit">
                Login <i className="fas fa-long-arrow-alt-right ml-2"></i>
              </Button>
            )}

          </Form>

          <div className="vignan_contact">
            Contact us:{" "}
            <span className="vignan_contact_value">
              <i className="fas fa-phone-alt"></i> {VIGNAN_CONTACT}
            </span>
          </div>
          </div>
        </div>

        {/* HIGHLIGHTS — feature blocks below the login */}
        <div className="vignan_highlights">
          <div className="vignan_block">
            <i className="fas fa-graduation-cap" />
            <h3>Expert Coaching</h3>
            <p>IIT-JEE, NEET &amp; SAT preparation guided by experienced faculty.</p>
          </div>
          <div className="vignan_block">
            <i className="fas fa-building" />
            <h3>Multiple Campuses</h3>
            <p>Strategically located across Andhra Pradesh &amp; Telangana.</p>
          </div>
          <div className="vignan_block">
            <i className="fas fa-users" />
            <h3>2000+ Students</h3>
            <p>A growing community achieving academic excellence every year.</p>
          </div>
          <div className="vignan_block">
            <i className="fas fa-chart-line" />
            <h3>Track Progress</h3>
            <p>Real-time exams with detailed performance analytics.</p>
          </div>
          <div className="vignan_block">
            <i className="fas fa-flask" />
            <h3>Modern Labs</h3>
            <p>Well-equipped science and computer labs for hands-on learning.</p>
          </div>
          <div className="vignan_block">
            <i className="fas fa-chalkboard-teacher" />
            <h3>Personal Mentoring</h3>
            <p>One-on-one guidance and regular doubt-clearing sessions.</p>
          </div>
          <div className="vignan_block">
            <i className="fas fa-trophy" />
            <h3>Proven Results</h3>
            <p>A consistent track record of top ranks in competitive exams.</p>
          </div>
          <div className="vignan_block">
            <i className="fas fa-laptop" />
            <h3>Smart Classrooms</h3>
            <p>Technology-enabled, digital learning environment.</p>
          </div>
        </div>

        {/* ABOUT — image + text */}
        <section className="vignan_about">
          <div className="vignan_about_img">
            <img src={imgAbout} alt="About Vignan Junior College" />
          </div>
          <div className="vignan_about_text">
            <h2>About <span className="accent">{VIGNAN_NAME}</span></h2>
            <p>
              Vignan Junior Colleges provide quality intermediate education, nurturing
              students with a strong foundation in academics, critical thinking, and
              holistic growth.
            </p>
            <p>
              With proven IIT-Advanced &amp; NEET faculty, scientific teaching methods,
              and modern residential infrastructure, we guide students toward top ranks
              in competitive examinations.
            </p>
          </div>
        </section>

        {/* COURSES — image cards */}
        <section className="vignan_courses">
          <h2 className="vignan_section_title">Courses <span className="accent">Offered</span></h2>
          <div className="vignan_course_grid">
            {VIGNAN_COURSES.map((course, i) => (
              <div className="vignan_course_card" key={i}>
                <div className="vignan_course_img">
                  <img src={course.img} alt={course.title} />
                </div>
                <div className="vignan_course_body">
                  <h3>{course.title}</h3>
                  <p>{course.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CAMPUSES — location chips */}
        <section className="vignan_campuses">
          <h2 className="vignan_section_title">Our <span className="accent">Campuses</span></h2>
          <p className="vignan_section_sub">Nine campuses across Andhra Pradesh &amp; Telangana</p>
          <div className="vignan_campus_list">
            {VIGNAN_CAMPUSES.map((campus, i) => (
              <span className="vignan_campus_chip" key={i}>
                <i className="fas fa-map-marker-alt" /> {campus}
              </span>
            ))}
          </div>
        </section>

        {/* BOTTOM — contact strip + copyright */}
        <footer className="vignan_footer">
          <div className="vignan_footer_info">
            <span><i className="fas fa-phone-alt" /> {VIGNAN_CONTACT}</span>
            <span><i className="fas fa-envelope" /> {VIGNAN_EMAIL}</span>
            <span><i className="fas fa-map-marker-alt" /> {VIGNAN_ADDRESS}</span>
          </div>
          <p className="vignan_footer_copy">&copy; 2026 {VIGNAN_NAME}. All rights reserved.</p>
          <p className="vignan_footer_brand">A product of <span>myLearningPlus</span></p>
        </footer>
      </div>
    );
  }
}

export default withRouter(
  graphql(LOGIN_USER, {
    name: "login1"
  })(VignanLogin)
);
